import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import AssigneePicker from './AssigneePicker';

const SubmittalDetailModal = ({ isOpen, onClose, submittal, token, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(submittal || {});
  const [loading, setLoading] = useState(false);

  if (!isOpen || !submittal) return null;

  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--monday-blue)', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submittals/${submittal.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          status: formData.status,
          assignee: formData.assignee
        })
      });
      
      if (response.ok) {
        onUpdate();
        setEditMode(false);
      }
    } catch (error) {
      console.error('Failed to update submittal:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{submittal.submittalId}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Title</label>
            {editMode ? (
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            ) : (
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{submittal.title}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            {editMode ? (
              <>
                <textarea
                  className="input"
                  rows="4"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <p className="form-helper">Add links by pasting URLs directly in the text</p>
              </>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {renderTextWithLinks(submittal.description)}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            {editMode ? (
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="Pending">Pending</option>
                <option value="In Review">In Review</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            ) : (
              <span className={`status-pill status-${submittal.status?.toLowerCase().replace(' ', '-')}`}>
                {submittal.status}
              </span>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Assignee</label>
            {editMode ? (
              <AssigneePicker
                assignees={formData.assignee ? [formData.assignee] : []}
                onChange={(assignees) => setFormData({ ...formData, assignee: assignees[0] || null })}
                token={token}
              />
            ) : (
              <div>{submittal.assignee || 'Not assigned'}</div>
            )}
          </div>
          {submittal.returnDate && (
            <div className="form-group">
              <label className="form-label">Return Date</label>
              <div>{new Date(submittal.returnDate).toLocaleDateString()}</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          {editMode ? (
            <>
              <button className="btn btn-secondary" onClick={() => setEditMode(false)} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                Edit
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmittalDetailModal;